import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Paper, useTheme, Divider } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Orientation, PageSize } from 'pdf-generator';
import { CapitalizeString } from '../../utils';

interface IframeDisplayProps {
    htmlContent: string;
    pageSize?: PageSize;
    orientation?: Orientation;
}

const pageDimensions: any = {
    A0: { portrait: { width: 841, height: 1189 }, landscape: { width: 1189, height: 841 } },
    A1: { portrait: { width: 594, height: 841 }, landscape: { width: 841, height: 594 } },
    A2: { portrait: { width: 420, height: 594 }, landscape: { width: 594, height: 420 } },
    A3: { portrait: { width: 297, height: 420 }, landscape: { width: 420, height: 297 } },
    A4: { portrait: { width: 210, height: 297 }, landscape: { width: 297, height: 210 } },
    A5: { portrait: { width: 148, height: 210 }, landscape: { width: 210, height: 148 } },
    A6: { portrait: { width: 105, height: 148 }, landscape: { width: 148, height: 105 } },
    Legal: { portrait: { width: 216, height: 356 }, landscape: { width: 356, height: 216 } },
    Letter: { portrait: { width: 216, height: 279 }, landscape: { width: 279, height: 216 } },
    Tabloid: { portrait: { width: 279, height: 432 }, landscape: { width: 432, height: 279 } },
};

const MM_TO_PX = 3.78; // 1mm = 3.78px

const HTMLViewer: React.FC<IframeDisplayProps> = ({ htmlContent, pageSize = 'A4', orientation = 'portrait' }) => {

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const [zoom, setZoom] = useState<number>(1);

    const calculateInitialZoom = () => {
        if (!containerRef.current) return 1;

        const containerWidth = containerRef.current.offsetWidth;
        const pageWidth = pageDimensions[pageSize][orientation].width * MM_TO_PX;

        // Set initial zoom to fit the width with some padding
        const targetWidth = containerWidth - 20; // 10px padding on each side
        return targetWidth / pageWidth;
    };

    useEffect(() => {
        // Set initial zoom after component mount and when page size/orientation changes
        const initialZoom = calculateInitialZoom();
        setZoom(initialZoom);

        // Add window resize handler to recalculate zoom
        const handleResize = () => {
            setZoom(calculateInitialZoom());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        // eslint-disable-next-line
    }, [pageSize, orientation]);


    useEffect(() => {
        if (iframeRef.current) {
            const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
            if (doc) {
                doc.open();
                doc.write(htmlContent);
                doc.close();

                const style = doc.createElement('style');
                style.textContent = `
          @page {
            size: ${pageSize} ${orientation};
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
        `;
                doc.head.appendChild(style);
            }
        }
        // eslint-disable-next-line
    }, [htmlContent, pageSize, orientation, zoom]);

    const handleZoomIn = () => setZoom(prevZoom => Math.min(prevZoom + 0.1, 3));
    const handleZoomOut = () => setZoom(prevZoom => Math.max(prevZoom - 0.1, 0.5));

    // Calculate the scaled dimensions
    const scaledWidth = pageDimensions[pageSize][orientation].width * MM_TO_PX * zoom; // Convert mm to px
    const scaledHeight = pageDimensions[pageSize][orientation].height * MM_TO_PX * zoom;

    return (
        <Box position="relative" width="100%" height="100%" overflow="hidden">
            <Paper elevation={3} sx={{ position: 'absolute', top: 10, right: 10, p: 1, zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}>
                    <IconButton color="primary" onClick={handleZoomOut}>
                        <ZoomOutIcon />
                    </IconButton>
                    <Typography variant="body1">{Math.round(zoom * 100)}%</Typography>
                    <IconButton color="primary" onClick={handleZoomIn}>
                        <ZoomInIcon />
                    </IconButton>
                </Box>

                <Divider orientation='horizontal' />
                <Box>
                    <Typography variant='caption'>Paper Size: <b>{pageSize}</b></Typography><br />
                    <Typography variant='caption'>Orientation: <b>{CapitalizeString(orientation)}</b></Typography>
                </Box>
            </Paper>

            <Box
                ref={containerRef}
                sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    background: theme => `1px solid ${theme.palette.divider}`,
                    // Add padding to ensure content doesn't get hidden behind the zoom controls
                    pt: 2
                }}
            >
                <Box sx={{
                    width: `${scaledWidth}px`,
                    height: `${scaledHeight}px`,
                    position: "relative",
                }}>

                    <iframe
                        ref={iframeRef}
                        style={{
                            width: pageDimensions[pageSize][orientation].width + "mm",
                            height: pageDimensions[pageSize][orientation].height + "mm",
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: theme.shadows[1],
                            background: theme.palette.background.paper,
                            transformOrigin: `top left`,
                            transform: `scale(${zoom})`,
                            position: "absolute",
                            top: 0,
                            left: 0
                        }}
                        title="HTML Preview"
                    ></iframe>
                </Box>
            </Box>
        </Box>
    );
};

export default HTMLViewer;
